import loggerHelper from './loggerHelper';
import { sendMailRequest } from './mailHelper';

export const INTAKE_STORAGE_KEY = 'devcraft_client_intake_draft';

export const BUSINESS_TYPES = [
    'Shop / Retail',
    'Clinic / Healthcare',
    'Restaurant / Cafe',
    'Consultant / Professional',
    'Real Estate',
    'Salon / Spa',
    'Gym / Fitness',
    'Photographer',
    'Education / Tutor',
    'Other',
];

export const VISITOR_ACTIONS = [
    { id: 'call', label: 'Call the business' },
    { id: 'whatsapp', label: 'WhatsApp message' },
    { id: 'form', label: 'Submit enquiry form' },
    { id: 'visit', label: 'Visit the shop / clinic' },
    { id: 'directions', label: 'Get directions / map' },
    { id: 'menu', label: 'See menu / services / prices' },
    { id: 'portfolio', label: 'See portfolio / past work' },
    { id: 'book', label: 'Book an appointment' },
    { id: 'buy', label: 'Buy / pay online' },
];

export const DISCOVERY_CHANNELS = [
    'Walk-ins',
    'Google Maps',
    'Instagram',
    'WhatsApp referrals',
    'Facebook',
    'Existing website',
    'Ads',
    'Other',
];

export const BUDGET_BANDS = [
    { id: 'express', label: 'Starter / Express (around ₹5,000 + domain & hosting)' },
    { id: 'growth', label: 'Growth (more pages, booking, light extras)' },
    { id: 'custom', label: 'Custom (payments, portal, larger system)' },
    { id: 'unsure', label: 'Not sure yet' },
];

export const INTAKE_STEPS = [
    { id: 'contact', title: 'Your business', subtitle: 'The essentials we need to begin' },
    { id: 'goals', title: 'Website needs', subtitle: 'Choose what visitors should be able to do' },
    { id: 'content', title: 'Ready to build', subtitle: 'Tell us what you already have' },
    { id: 'review', title: 'Confirm', subtitle: 'Check the details and send your request' },
];

export const getEmptyIntakeForm = () => ({
    customerName: '',
    businessName: '',
    role: 'Owner',
    phone: '',
    email: '',
    preferredContact: 'WhatsApp',
    locality: '',
    areasServed: '',
    preferredLanguage: 'Hinglish',

    businessType: '',
    businessDescription: '',
    mainServices: '',
    topServices: '',
    yearsOperating: '',
    whyCustomersChoose: '',
    mainChallenge: '',

    idealCustomer: '',
    customerLocation: '',
    customerProblem: '',
    purchaseObjections: '',
    discoveryChannels: [],
    mainContactChannel: 'WhatsApp',

    whyWebsiteNow: '',
    visitorActions: [],
    successDefinition: '',
    hasWebsite: 'No',
    websiteUrl: '',
    hasGoogleBusiness: 'Not sure',
    googleBusinessUrl: '',
    hasInstagram: 'No',
    instagramUrl: '',
    hasDomain: 'No',

    hasBusinessPhone: true,
    hasAddressOrMaps: false,
    hasServicesList: false,
    hasPhotos: false,
    hasLogo: false,
    hasTimings: false,
    contentReadyIn: '1-2 days',
    contentNotes: '',

    budgetBand: 'express',
    goLiveWhen: 'As soon as possible',
    decisionMaker: '',
    needsOnlinePayments: false,
    needsBookingSystem: false,
    needsMultiLocation: false,
    needsMultiLanguage: false,
    needsCrmOrPortal: false,
    extraNotes: '',
});

export const classifyOpportunity = (form) => {
    const customSignals = [
        form.needsOnlinePayments,
        form.needsBookingSystem,
        form.needsMultiLocation,
        form.needsMultiLanguage,
        form.needsCrmOrPortal,
        form.budgetBand === 'custom',
        form.visitorActions.includes('buy'),
        form.visitorActions.includes('book') && form.needsBookingSystem,
    ].filter(Boolean).length;

    const contentReady =
        form.hasBusinessPhone &&
        (form.hasAddressOrMaps || Boolean(form.locality.trim())) &&
        form.hasServicesList;

    if (customSignals >= 2 || form.budgetBand === 'custom') {
        return {
            type: 'Custom / Growth',
            reason:
                'Needs system-level features (payments, booking, multi-location, portal) or a custom budget band.',
            expressFit: false,
        };
    }

    if (form.budgetBand === 'growth' || customSignals === 1) {
        return {
            type: 'Express + Phase 2',
            reason:
                'Can launch with Express first; one or more extras can follow after go-live.',
            expressFit: true,
        };
    }

    if (!contentReady) {
        return {
            type: 'Express (content pending)',
            reason:
                'Likely Express fit, but phone/address/services checklist is incomplete — delivery clock starts after content arrives.',
            expressFit: true,
        };
    }

    return {
        type: 'DevCraft Express',
        reason: 'Local presence site with call/WhatsApp goals fits the ₹5,000 / 3-day starter package.',
        expressFit: true,
    };
};

export const validateStep = (stepId, form) => {
    const errors = {};

    if (stepId === 'contact') {
        if (!form.customerName.trim()) errors.customerName = 'Name is required';
        if (!form.businessName.trim()) errors.businessName = 'Business name is required';
        if (!form.phone.trim()) errors.phone = 'WhatsApp / phone is required';
        if (!form.locality.trim()) errors.locality = 'Locality / city is required';
        if (!form.businessType) errors.businessType = 'Select a business type';
    }

    if (stepId === 'goals') {
        if (!form.mainServices.trim()) errors.mainServices = 'Add your main services or products';
        if (!form.visitorActions.length) errors.visitorActions = 'Pick at least one visitor action';
    }

    return errors;
};

export const buildIntakeSummary = (form) => {
    const classification = classifyOpportunity(form);
    const channels = form.discoveryChannels.length
        ? form.discoveryChannels.join(', ')
        : 'Not specified';
    const actions = form.visitorActions.length
        ? form.visitorActions
            .map((id) => VISITOR_ACTIONS.find((item) => item.id === id)?.label || id)
            .join(', ')
        : 'Not specified';

    const lines = [
        'DEVCRAFT WEBSITE REQUEST',
        '========================',
        `Submitted: ${new Date().toLocaleString('en-IN')}`,
        `Classification: ${classification.type}`,
        `Why: ${classification.reason}`,
        '',
        '1. CONTACT',
        `Name: ${form.customerName}`,
        `Business: ${form.businessName}`,
        `Role: ${form.role}`,
        `Phone / WhatsApp: ${form.phone}`,
        `Email: ${form.email || '—'}`,
        `Preferred contact: ${form.preferredContact}`,
        `Locality: ${form.locality}`,
        `Areas served: ${form.areasServed || '—'}`,
        `Language: ${form.preferredLanguage}`,
        '',
        '2. BUSINESS',
        `Type: ${form.businessType}`,
        `Description: ${form.businessDescription}`,
        `Main services: ${form.mainServices}`,
        `Top / profitable: ${form.topServices || '—'}`,
        `Operating: ${form.yearsOperating || '—'}`,
        `Why customers choose them: ${form.whyCustomersChoose || '—'}`,
        `Main challenge: ${form.mainChallenge || '—'}`,
        '',
        '3. CUSTOMERS & ACQUISITION',
        `Ideal customer: ${form.idealCustomer}`,
        `Customer location: ${form.customerLocation || '—'}`,
        `Problem they solve: ${form.customerProblem || '—'}`,
        `Objections: ${form.purchaseObjections || '—'}`,
        `How customers find them: ${channels}`,
        `Main contact channel today: ${form.mainContactChannel}`,
        '',
        '4. WEBSITE GOALS',
        `Why now: ${form.whyWebsiteNow}`,
        `Desired visitor actions: ${actions}`,
        `Success looks like: ${form.successDefinition || '—'}`,
        `Existing website: ${form.hasWebsite}${form.websiteUrl ? ` (${form.websiteUrl})` : ''}`,
        `Google Business: ${form.hasGoogleBusiness}${form.googleBusinessUrl ? ` (${form.googleBusinessUrl})` : ''}`,
        `Instagram: ${form.hasInstagram}${form.instagramUrl ? ` (${form.instagramUrl})` : ''}`,
        `Owns domain: ${form.hasDomain}`,
        '',
        '5. CONTENT CHECKLIST',
        `Phone ready: ${form.hasBusinessPhone ? 'Yes' : 'No'}`,
        `Address / Maps: ${form.hasAddressOrMaps ? 'Yes' : 'No'}`,
        `Services list: ${form.hasServicesList ? 'Yes' : 'No'}`,
        `Photos: ${form.hasPhotos ? 'Yes' : 'No'}`,
        `Logo: ${form.hasLogo ? 'Yes' : 'No'}`,
        `Timings: ${form.hasTimings ? 'Yes' : 'No'}`,
        `Content ready in: ${form.contentReadyIn}`,
        `Content notes: ${form.contentNotes || '—'}`,
        '',
        '6. COMMERCIAL',
        `Budget band: ${BUDGET_BANDS.find((b) => b.id === form.budgetBand)?.label || form.budgetBand}`,
        `Go-live: ${form.goLiveWhen}`,
        `Decision maker: ${form.decisionMaker}`,
        `Needs online payments: ${form.needsOnlinePayments ? 'Yes' : 'No'}`,
        `Needs booking system: ${form.needsBookingSystem ? 'Yes' : 'No'}`,
        `Needs multi-location: ${form.needsMultiLocation ? 'Yes' : 'No'}`,
        `Needs multi-language: ${form.needsMultiLanguage ? 'Yes' : 'No'}`,
        `Needs CRM / portal: ${form.needsCrmOrPortal ? 'Yes' : 'No'}`,
        `Extra notes: ${form.extraNotes || '—'}`,
        '',
        'Express included reminder: 4 pages, mobile-first, SEO basics, click-to-call + form. Domain & hosting paid separately.',
    ];

    return lines.join('\n');
};

export const saveIntakeDraft = (form) => {
    try {
        localStorage.setItem(INTAKE_STORAGE_KEY, JSON.stringify(form));
        loggerHelper.debug('Intake draft saved');
    } catch (error) {
        loggerHelper.warn('Could not save intake draft', error);
    }
};

export const loadIntakeDraft = () => {
    try {
        const raw = localStorage.getItem(INTAKE_STORAGE_KEY);
        if (!raw) return null;
        return { ...getEmptyIntakeForm(), ...JSON.parse(raw) };
    } catch (error) {
        loggerHelper.warn('Could not load intake draft', error);
        return null;
    }
};

export const clearIntakeDraft = () => {
    try {
        localStorage.removeItem(INTAKE_STORAGE_KEY);
        loggerHelper.debug('Intake draft cleared');
    } catch (error) {
        loggerHelper.warn('Could not clear intake draft', error);
    }
};

export const submitIntakeBrief = async (form) => {
    const summary = buildIntakeSummary(form);
    const classification = classifyOpportunity(form);
    const subject = `Website request – ${form.businessName} – ${classification.type}`;

    try {
        localStorage.setItem(
            `devcraft_client_intake_submitted_${Date.now()}`,
            JSON.stringify({ form, summary, classification, submittedAt: new Date().toISOString() })
        );
    } catch (error) {
        loggerHelper.warn('Could not cache submitted intake', error);
    }

    loggerHelper.info('Submitting website request via MGenie mail API', {
        businessName: form.businessName,
        classification: classification.type,
    });

    const result = await sendMailRequest({
        subject,
        summary,
        customerName: form.customerName,
        customerEmail: form.email || undefined,
        businessName: form.businessName,
        phone: form.phone || undefined,
        inquiryType: 'website-request',
    });

    return { summary, classification, subject, messageId: result.messageId || null };
};
